<template>
    <div
        id="picture-input"
        ref="container"
        class="picture-input ImageInput"
        :class="draggingOver ? 'ImageInput--dragging-over' : ''"
    >
        <div v-if="!supportsUpload" v-html="compiledStrings.upload"></div>
        <div v-else-if="supportsPreview">
            <div
                class="preview-container"
                :style="{
                    maxWidth: previewWidth + 'px',
                    height: previewHeight + 'px',
                    borderRadius: radius + '%',
                }"
            >
                <canvas
                    ref="previewCanvas"
                    class="picture-preview"
                    :class="computedClasses"
                    :style="{ height: previewHeight + 'px', zIndex: zIndex + 1 }"
                    @drag.stop.prevent=""
                    @dragover.stop.prevent=""
                    @dragstart.stop.prevent="onDragStart"
                    @dragenter.stop.prevent="onDragStart"
                    @dragend.stop.prevent="onDragStop"
                    @dragleave.stop.prevent="onDragStop"
                    @drop.stop.prevent="onFileDrop"
                    @click.prevent="onClick"
                >
                </canvas>
                <div
                    v-if="!imageSelected && !plain"
                    class="picture-inner"
                    :style="{
                        top: -previewHeight + 'px',
                        marginBottom: -previewHeight + 'px',
                        borderRadius: radius + '%',
                        zIndex: zIndex + 2,
                    }"
                    :class="disabled ? 'picture-inner--disabled' : ''"
                >
                    <span
                        v-if="disabled && supportsDragAndDrop"
                        class="picture-inner-text"
                    ></span>
                    <span v-if="!disabled && supportsDragAndDrop" 
                        class="picture-inner-text" 
                        v-html="compiledStrings.drag">
                    </span>
                    <span v-if="!supportsDragAndDrop" 
                        class="picture-inner-text" 
                        v-html="compiledStrings.tap">
                    </span>
                </div>

                <template v-if="imageSelected && !disabled">
                    <div class="ImageInput__Edit btn-group dropup">
                        <button
                            type="button"
                            class="btn btn-default dropdown-toggle"
                            data-toggle="dropdown"
                            aria-expanded="false"
                            :style="{ zIndex: zIndex + 1 }"
                        >
                            <i class="fa fa-pencil"></i> Rediger
                        </button>

                        <ul class="dropdown-menu dropdown-menu-right">
                            <li>
                                <a @click.prevent="selectImage">
                                    <i class="fa fa-btn fa-cloud-upload"></i> Upload nyt
                                </a>
                            </li>
                            <li class="divider" v-if="editable"></li>
                            <li v-if="editable">
                                <a @click.prevent="editImage">
                                  <i class="fa fa-btn fa-pencil"></i> Rediger
                                </a>
                            </li>
                            <li class="divider"></li>
                            <li>
                                <a @click.prevent="removeImage">
                                    <i class="fa fa-btn fa-times"></i> Fjern
                                </a>
                            </li>
                        </ul>
                    </div>
                </template>
            </div>
        </div>
        <div v-else>
            <button v-if="!imageSelected" :class="buttonClass" @click.prevent="selectImage">
                {{ compiledStrings.select }}
            </button>
            <div v-else>
                <div v-html="compiledStrings.selected"></div>
                <button :class="buttonClass" @click.prevent="selectImage">
                    {{ compiledStrings.change }}
                </button>
                <button v-if="removable" :class="removeButtonClass" @click.prevent="removeImage">
                    {{ compiledStrings.remove }}
                </button>
            </div>
        </div>
        <input
            :id="id"
            ref="fileInput"
            type="file"
            :name="name"
            :accept="accept"
            @change="onFileChange"
            :disabled="disabled"
        />
    </div>
</template>

<script>
import HasEventListeners from 'dashboard-js/mixins/HasEventListeners'

export default {
    name: 'PictureInput',
    mixins: [HasEventListeners],
    props: {
        width: {
            type: [String, Number],
            default: Number.MAX_SAFE_INTEGER,
        },
        height: {
            type: [String, Number],
            default: Number.MAX_SAFE_INTEGER,
        },
        margin: {
            type: [String, Number],
            default: 0,
        },
        accept: {
            type: String,
            default: 'image/*',
        },
        size: {
            type: [String, Number],
            default: 65, //Number.MAX_SAFE_INTEGER
        },
        name: {
            type: String,
            default: null,
        },
        id: {
            type: [String, Number],
            default: null,
        },
        buttonClass: {
            type: String,
            default: 'btn btn-default',
        },
        removeButtonClass: {
            type: String,
            default: 'btn btn-danger',
        },
        aspectButtonClass: {
            type: String,
            default: 'btn btn-default',
        },
        prefill: {
            type: [String, File],
            default: '',
        },
        prefillOptions: {
            type: Object,
            default: () => {
                return {
                    name: 'initial.jpg',
                    mediaType: 'image/jpeg',
                }
            },
        },
        crop: {
            type: Boolean,
            default: true,
        },
        radius: {
            type: [String, Number],
            default: 3,
        },
        removable: {
            type: Boolean,
            default: true,
        },
        editable: {
            type: Boolean,
            default: false,
        },
        autoToggleAspectRatio: {
            type: Boolean,
            default: false,
        },
        toggleAspectRatio: {
            type: Boolean,
            default: false,
        },
        changeOnClick: {
            type: Boolean,
            default: true,
        },
        plain: {
            type: Boolean,
            default: false,
        },
        zIndex: {
            type: Number,
            default: 100,
        },
        strings: {
            type: Object,
            default: () => {
                return {}
            },
        },
        disabled: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            imageSelected: false,
            previewHeight: 0,
            previewWidth: 0,
            draggingOver: false,
            canvasWidth: 0,
            canvasHeight: 0,
            compiledStrings: {
                upload: '<p>Your device does not support file uploading.</p>',
                drag: 'Træk billede hertil eller klik for at uploade.', //'Drag an image or <br>click here to select a file',
                tap: 'Klik her for at uploade fra dit galleri', //'Tap here to select a photo <br>from your gallery',
                change: 'Upload nyt', //'Change Photo',
                aspect: 'Landscape/Portrait',
                remove: 'Fjern', //'Remove Photo',
                edit: 'Rediger', // 'Edit Photo'
                select: 'Vælg billede', //'Select a Photo',
                selected: '<p>Billede valgt!</p>', // '<p>Photo successfully selected!</p>'
                fileSize: 'Filen er for stor', //'The file size exceeds the limit',
                fileType: 'Filtypen er ikke understøttet', //'This file type is not supported.'
            },
        }
    },
    computed: {
        supportsUpload() {
            if (
                navigator.userAgent.match(
                    /(Android (1.0|1.1|1.5|1.6|2.0|2.1))|(Windows Phone (OS 7|8.0))|(XBLWP)|(ZuneWP)|(w(eb)?OSBrowser)|(webOS)|(Kindle\/(1.0|2.0|2.5|3.0))/
                )
            ) {
                return false
            }
            const el = document.createElement('input')
            el.type = 'file'
            return !el.disabled
        },
        supportsPreview() {
            return window.FileReader && !!window.CanvasRenderingContext2D
        },
        supportsDragAndDrop() {
            const div = document.createElement('div')
            return (
                ('draggable' in div || ('ondragstart' in div && 'ondrop' in div)) &&
                !('ontouchstart' in window || navigator.msMaxTouchPoints)
            )
        },
        computedClasses() {
            const classObject = {}
            classObject['dragging-over'] = this.draggingOver
            return classObject
        },
    },
    watch: {
        prefill() {
            if (this.prefill) {
                this.preloadImage(this.prefill, this.prefillOptions)
            } else {
                this.removeImage()
            }
        },
    },
    mounted() {
        this.updateStrings()
        if (this.prefill) {
            this.preloadImage(this.prefill, this.prefillOptions)
        }

        this.$nextTick(() => {
            this.listen(window, 'resize', this.onResize)
            this.onResize()
        })

        if (this.supportsPreview) {
            this.pixelRatio = Math.round(
                window.devicePixelRatio || window.screen.deviceXDPI / window.screen.logicalXDPI
            )
            const canvas = this.$refs.previewCanvas
            if (canvas.getContext) {
                this.context = canvas.getContext('2d')
                this.context.scale(this.pixelRatio, this.pixelRatio)
            }
        }
        if (this.accept !== 'image/*') {
            this.fileTypes = this.accept.split(',')
            this.fileTypes = this.fileTypes.map(s => s.trim())
        }

        this.canvasWidth = this.width
        this.canvasHeight = this.height

        Bus.$on('tabChanged', () => setTimeout(() => this.onResize()))
    },
    methods: {
        updateStrings() {
            for (let s in this.strings) {
                if (s in this.compiledStrings && typeof this.strings[s] === 'string') {
                    this.compiledStrings[s] = this.strings[s]
                }
            }
        },
        onClick() {
            if (!this.imageSelected) {
                this.selectImage()
                return
            }

            if (this.changeOnClick) {
                this.selectImage()
            }

            this.$emit('click')
        },
        onResize() {
            // Hotfix: There are some buggy event listeners that are not cleaned up properly.
            // This is nasty fix to patch that for now.
            if (!this.$refs.container) {
                return
            }

            this.resizeCanvas()

            if (this.imageObject) {
                this.drawImage(this.imageObject)
            }
        },
        onDragStart() {
            if (!this.supportsDragAndDrop) {
                return
            }
            this.draggingOver = true
        },
        onDragStop() {
            if (!this.supportsDragAndDrop) {
                return
            }
            this.draggingOver = false
        },
        onFileDrop(e) {
            this.onDragStop()
            this.onFileChange(e)
        },
        onFileChange(e, preloaded = false) {
            let files = e.target.files || e.dataTransfer.files
            if (!files.length) {
                return
            }
            if (files[0].size <= 0 || files[0].size > this.size * 1024 * 1024) {
                alert(this.compiledStrings.fileSize + ' (' + this.size + 'MB)')
                return
            }
            if (
                files[0].name === this.fileName &&
                files[0].size === this.fileSize &&
                this.fileModified === files[0].lastModified
            ) {
                return
            }

            this.file = files[0]
            this.fileName = files[0].name
            this.fileSize = files[0].size
            this.fileModified = files[0].lastModified
            this.fileType = files[0].type
            if (this.accept === 'image/*') {
                if (files[0].type.substr(0, 6) !== 'image/') {
                    return
                }
            } else {
                if (this.fileTypes.indexOf(files[0].type) === -1) {
                    alert(this.compiledStrings.fileType)
                    return
                }
            }
            this.imageSelected = true
            this.image = ''

            if (this.supportsPreview) {
                this.loadImage(files[0])
            }

            if (!preloaded) {
                this.emitChange()
            }
        },
        loadImage(file) {
            this.getEXIFOrientation(file, orientation => {
                this.setOrientation(orientation)
                let reader = new FileReader()
                reader.onload = e => {
                    this.image = e.target.result
                    this.imageObject = new Image()
                    this.imageObject.onload = () => {
                        if (this.autoToggleAspectRatio) {
                            let canvasOrientation = this.getOrientation(
                                this.canvasWidth,
                                this.canvasHeight
                            )
                            let imageOrientation = this.getOrientation(
                                this.imageObject.width,
                                this.imageObject.height
                            )

                            if (canvasOrientation !== imageOrientation) {
                                this.rotateCanvas()
                            }
                        }

                        this.drawImage(this.imageObject)
                    }
                    this.imageObject.src = this.image
                }
                reader.readAsDataURL(file)
            })
        },
        drawImage(image) {
            if (!this.$refs.previewCanvas) {
                return
            }

            this.$emit('draw')

            this.imageWidth = image.width
            this.imageHeight = image.height
            this.imageRatio = image.width / image.height
            let offsetX = 0
            let offsetY = 0
            let scaledWidth = this.previewWidth
            let scaledHeight = this.previewHeight
            if (this.crop) {
                if (this.imageRatio > 1) {
                    scaledWidth = scaledHeight * this.imageRatio
                    offsetX = (this.previewWidth - scaledWidth) / 2
                } else {
                    scaledHeight = scaledWidth / this.imageRatio
                    offsetY = (this.previewHeight - scaledHeight) / 2
                }
            } else {
                if (this.imageRatio > 1) {
                    scaledHeight = scaledWidth / this.imageRatio
                    offsetY = (this.previewHeight - scaledHeight) / 2
                } else {
                    scaledWidth = scaledHeight * this.imageRatio
                    offsetX = (this.previewWidth - scaledWidth) / 2
                }
            }
            const canvas = this.$refs.previewCanvas
            canvas.style.background = 'none'
            canvas.width = this.previewWidth * this.pixelRatio
            canvas.height = this.previewHeight * this.pixelRatio
            this.context.setTransform(1, 0, 0, 1, 0, 0)
            this.context.clearRect(0, 0, canvas.width, canvas.height)

            this.$emit('ready')

            if (this.rotate) {
                this.context.translate(offsetX * this.pixelRatio, offsetY * this.pixelRatio)
                this.context.translate(
                    (scaledWidth / 2) * this.pixelRatio,
                    (scaledHeight / 2) * this.pixelRatio
                )
                this.context.rotate(this.rotate)
                offsetX = -scaledWidth / 2
                offsetY = -scaledHeight / 2
            }
            this.context.drawImage(
                image,
                offsetX * this.pixelRatio,
                offsetY * this.pixelRatio,
                scaledWidth * this.pixelRatio,
                scaledHeight * this.pixelRatio
            )
        },
        emitChange() {
            this.$emit('change', { file: this.file })
        },
        selectImage() {
            this.$refs.fileInput.click()
        },
        editImage() {
          this.$emit('edit', { file: this.file })
        },
        removeImage() {
            this.$refs.fileInput.value = ''
            this.$refs.fileInput.type = ''
            this.$refs.fileInput.type = 'file'
            this.fileName = ''
            this.fileType = ''
            this.fileSize = 0
            this.fileModified = 0
            this.imageSelected = false
            this.image = ''
            this.file = null
            this.imageObject = null
            this.$refs.previewCanvas.style.backgroundColor = 'transparent'
            this.$refs.previewCanvas.width = this.previewWidth * this.pixelRatio
            this.$emit('remove')
            this.emitChange()
        },
        rotateImage() {
            this.rotateCanvas()

            if (this.imageObject) {
                this.drawImage(this.imageObject)
            }

            let newOrientation = this.getOrientation(this.canvasWidth, this.canvasHeight)
            this.$emit('aspectratiochange', newOrientation)
        },
        resizeCanvas() {
            let previewRatio = this.canvasWidth / this.canvasHeight
            let newWidth = this.$refs.container.clientWidth
            if (!this.toggleAspectRatio && newWidth === this.containerWidth) {
                return
            }
            this.containerWidth = newWidth
            this.previewWidth = Math.min(this.containerWidth - this.margin * 2, this.canvasWidth)
            this.previewHeight = this.previewWidth / previewRatio
        },
        getOrientation(width, height) {
            let orientation = 'square'

            if (width > height) {
                orientation = 'landscape'
            } else if (width < height) {
                orientation = 'portrait'
            }

            return orientation
        },
        switchCanvasOrientation() {
            const canvasWidth = this.canvasWidth
            const canvasHeight = this.canvasHeight

            this.canvasWidth = canvasHeight
            this.canvasHeight = canvasWidth
        },
        rotateCanvas() {
            this.switchCanvasOrientation()
            this.resizeCanvas()
        },
        setOrientation(orientation) {
            this.rotate = false
            if (orientation === 8) {
                this.rotate = -Math.PI / 2
            } else if (orientation === 6) {
                this.rotate = Math.PI / 2
            } else if (orientation === 3) {
                this.rotate = -Math.PI
            }
        },
        getEXIFOrientation(file, callback) {
            var reader = new FileReader()
            reader.onload = e => {
                var view = new DataView(e.target.result)
                if (view.getUint16(0, false) !== 0xffd8) {
                    return callback(-2)
                }
                var length = view.byteLength
                var offset = 2
                while (offset < length) {
                    var marker = view.getUint16(offset, false)
                    offset += 2
                    if (marker === 0xffe1) {
                        if (view.getUint32((offset += 2), false) !== 0x45786966) {
                            return callback(-1)
                        }
                        var little = view.getUint16((offset += 6), false) === 0x4949
                        offset += view.getUint32(offset + 4, little)
                        var tags = view.getUint16(offset, little)
                        offset += 2
                        for (var i = 0; i < tags; i++) {
                            if (view.getUint16(offset + i * 12, little) === 0x0112) {
                                return callback(view.getUint16(offset + i * 12 + 8, little))
                            }
                        }
                    } else if ((marker & 0xff00) !== 0xff00) {
                        break
                    } else {
                        offset += view.getUint16(offset, false)
                    }
                }
                return callback(-1)
            }
            reader.readAsArrayBuffer(file.slice(0, 65536))
        },
        preloadImage(source, options) {
            options = Object.assign({}, options)
            if (typeof source === 'object') {
                this.imageSelected = true
                this.image = ''

                if (this.supportsPreview) {
                    this.loadImage(source)
                }
                //                    else {
                //                        this.$emit('change')
                //                    }
                return
            }

            // Force https scheme
            source = source.replace('http://', 'https://')

            let headers = new Headers()
            headers.append('Accept', 'image/*')
            fetch(source, {
                method: 'GET',
                mode: 'cors',
                headers: headers,
            })
                .then(response => {
                    return response.blob()
                })
                .then(imageBlob => {
                    let e = { target: { files: [] } }
                    const fileName = options.fileName || source.split('/').slice(-1)[0]
                    let mediaType =
                        options.mediaType ||
                        'image/' + (options.fileType || fileName.split('.').slice(-1)[0])
                    mediaType = mediaType.replace('jpg', 'jpeg')
                    e.target.files[0] = new File([imageBlob], fileName, { type: mediaType })
                    this.onFileChange(e, true)
                })
                .catch(() => {
                    //
                })
        },
    },
}
</script>

<style scoped type="text/less" lang="less">
@import "~dashboard-less/variables";
.preview-container {
    box-sizing: border-box;
    cursor: pointer;
    overflow: hidden;
    position: relative;
}

.picture-preview {
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 10001;
    box-sizing: border-box;
    background-color: transparent; /*rgba(200, 200, 200, .25);*/
}

.picture-inner {
    position: relative;
    z-index: 10002;
    pointer-events: none;
    box-sizing: border-box;
    margin: 0 auto;
    padding: 10px;
    border: 2px dashed #becfdc;
    border-radius: 8px;
    width: calc(~'100% - 6px');
    height: calc(~'100% - 6px');
    display: flex;
    background-color: white;
}

.picture-inner .picture-inner-text {
    flex: 1;
    align-self: center;
    text-align: center;
    line-height: 1.5;
}

.picture-inner--disabled {
    background: #e5e7eb !important;
}

input[type='file'] {
    display: none;
}

.ImageInput--dragging-over {
    .picture-inner {
        border: 4px dashed #85c0fd;
    }
    .picture-preview {
        filter: blur(5px) !important;
    }
}

.ImageInput .ImageInput__Edit {
    &.btn-group {
        position: absolute;
        bottom: 0;
        width: 100%;
    }

    .btn {
        padding: 2px 9px;
        opacity: 0.92;
        border-color: transparent;
        border-radius: 0;
        border-top-left-radius: 6px;
        float: right;
    }

    .dropdown-menu {
        width: 100%;
        max-width: 200px;
        min-width: 0;
        padding: 3px 0;

        a {
            cursor: pointer;
        }

        .divider {
            margin: 3px 0;
        }
    }
    @media (max-width: @screen-xs-max) {
        .btn {
            float: none;
            display: block;
            margin: auto;
            font-size: 8px;
            border-top-right-radius: 6px;
        }
    }
}

</style>
